/**
 * Global UX patch — auto-select a number input's contents when it's focused
 * and the current value is the placeholder "0" (or "0.00" / "0.0" / "-0" / "0,00" etc).
 * That way the user's next keystroke REPLACES the zero instead of appending
 * (so "0" + typing "5" → "5", not "05").
 *
 * Attaches one passive document-level focusin listener; covers every
 * <input type="number"> in the app — including third-party form components —
 * without modifying their source.
 *
 * No-op for non-numeric inputs, non-zero values, or readonly/disabled fields.
 */

const ZERO_PATTERNS = /^-?0+([.,]0+)?$/;

function selectIfZero(el: HTMLInputElement) {
  if (el.readOnly || el.disabled) return;
  const v = el.value;
  if (!v) return;
  if (!ZERO_PATTERNS.test(v.trim())) return;
  // rAF — let the browser finish the focus event (caret placement)
  // before we overwrite the selection. Without this, some browsers
  // re-place the caret after our select() call.
  requestAnimationFrame(() => {
    // Re-check: the element may have lost focus or value may have changed
    // before the rAF fires (rare but possible during fast keyboard nav).
    if (document.activeElement !== el) return;
    if (!ZERO_PATTERNS.test(el.value.trim())) return;
    try { el.select(); } catch { /* select() can throw on some input types — ignore */ }
  });
}

function handleFocusIn(e: Event) {
  const target = e.target as HTMLElement | null;
  if (!target || target.tagName !== 'INPUT') return;
  const input = target as HTMLInputElement;
  if (input.type !== 'number') return;
  selectIfZero(input);
}

let installed = false;

export function installNumberInputAutoSelect() {
  if (installed || typeof document === 'undefined') return;
  document.addEventListener('focusin', handleFocusIn, true);
  installed = true;
}
